import { useState, useRef, useEffect } from 'react';
import { Mail, X, FileText, Eye, Download, Trash2, UploadCloud, Loader2, AlertCircle, RotateCcw } from 'lucide-react';
import { showSuccessToast, showErrorToast } from '../../../components/Toast/Toaster';

const BRAND_RED = '#EC171C';
const GRID_COLS = '30px 1.3fr 1.1fr 0.9fr 1.6fr';
const GRID_GAP = '14px';
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const validateFile = (file) => {
  if (!ALLOWED_TYPES.includes(file.type)) return 'Only JPG, PNG, and PDF files are allowed.';
  if (file.size > MAX_FILE_SIZE) return 'File exceeds the 10 MB limit.';
  return null;
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const IconBtn = ({ onClick, title, danger, disabled, children }) => {
  const [hov, setHov] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      disabled={disabled}
      onMouseEnter={() => !disabled && setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: '1px solid #e5e7eb', borderRadius: 6,
        background: disabled ? '#f9fafb' : hov && danger ? '#fee2e2' : hov ? '#f3f4f6' : '#fff',
        color: disabled ? '#d1d5db' : hov && danger ? '#dc2626' : '#6b7280',
        cursor: disabled ? 'not-allowed' : 'pointer', transition: 'all 0.15s', flexShrink: 0,
      }}
    >
      {children}
    </button>
  );
};

const UploadButton = ({ onClick, disabled }) => {
  const [hov, setHov] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => !disabled && setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 7,
        border: `1.5px dashed ${hov ? BRAND_RED : '#d1d5db'}`,
        borderRadius: 9, padding: '11px 14px',
        background: hov ? '#fef5f5' : 'transparent',
        color: hov ? BRAND_RED : '#6b7280',
        fontSize: 13, fontWeight: 500, cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.15s', fontFamily: 'inherit', opacity: disabled ? 0.5 : 1,
      }}
    >
      <UploadCloud size={15} />
      Upload COD statement
    </button>
  );
};

const AttachmentCell = ({ uploadState, onPickFile, onRetryUpload, onView, onDownload, onRemove, locked }) => {
  const { phase, file, errorMsg } = uploadState;

  if (phase === 'empty') {
    return <UploadButton onClick={onPickFile} disabled={locked} />;
  }

  if (phase === 'selected') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #dbeafe', borderRadius: 9, padding: '8px 10px', minWidth: 0, background: '#f0f7ff' }}>
        <div style={{ width: 30, height: 30, borderRadius: 6, background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <FileText size={14} color="#3b82f6" />
        </div>
        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <div style={{ fontSize: 12.5, fontWeight: 500, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{file?.name}</div>
          <div style={{ fontSize: 11, color: '#3b82f6', whiteSpace: 'nowrap' }}>
            Ready to upload <span style={{ color: '#9ca3af' }}>· {formatFileSize(file?.size)}</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
          <IconBtn onClick={onView} title="View" disabled={locked}><Eye size={14} /></IconBtn>
          <IconBtn onClick={onDownload} title="Download" disabled={locked}><Download size={14} /></IconBtn>
          <IconBtn onClick={onRemove} title="Remove" danger disabled={locked}><Trash2 size={14} /></IconBtn>
        </div>
      </div>
    );
  }

  if (phase === 'uploading') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #e5e7eb', borderRadius: 9, padding: '8px 10px', background: '#fafafa' }}>
        <div style={{ width: 30, height: 30, borderRadius: 6, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Loader2 size={14} className="animate-spin" color="#6b7280" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 500, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{file?.name}</div>
          <div style={{ fontSize: 11, color: '#9ca3af' }}>Uploading…</div>
        </div>
      </div>
    );
  }

  if (phase === 'uploaded') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #e5e7eb', borderRadius: 9, padding: '8px 10px', minWidth: 0 }}>
        <div style={{ width: 30, height: 30, borderRadius: 6, background: '#fde7e7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <FileText size={14} color={BRAND_RED} />
        </div>
        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <div style={{ fontSize: 12.5, fontWeight: 500, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{file?.name}</div>
          <div style={{ fontSize: 11, color: '#16a34a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            ✓ Uploaded <span style={{ color: '#9ca3af' }}>· {formatFileSize(file?.size)}</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
          <IconBtn onClick={onView} title="View" disabled={locked}><Eye size={14} /></IconBtn>
          <IconBtn onClick={onDownload} title="Download" disabled={locked}><Download size={14} /></IconBtn>
          <IconBtn onClick={onRemove} title="Remove" danger disabled={locked}><Trash2 size={14} /></IconBtn>
        </div>
      </div>
    );
  }

  // upload_failed
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #fca5a5', borderRadius: 9, padding: '8px 10px', background: '#fef2f2', minWidth: 0 }}>
      <div style={{ width: 30, height: 30, borderRadius: 6, background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <AlertCircle size={14} color="#dc2626" />
      </div>
      <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
        <div style={{ fontSize: 12.5, fontWeight: 500, color: '#dc2626' }}>Upload failed</div>
        <div style={{ fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{file?.name || errorMsg}</div>
      </div>
      <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
        <IconBtn onClick={onRetryUpload} title="Retry"><RotateCcw size={14} /></IconBtn>
        <IconBtn onClick={onRemove} title="Remove" danger><Trash2 size={14} /></IconBtn>
      </div>
    </div>
  );
};

// ─── Main modal ──────────────────────────────────────────────────────────────

const mkEmpty = () => ({ phase: 'empty', file: null, objectUrl: null, attachmentId: null, attachmentUrl: null, errorMsg: null });

const BulkEmailModal = ({ rows, onClose, uploadAttachment }) => {
  const [uploadStates, setUploadStates] = useState(() => {
    const s = {};
    rows.forEach((r) => { s[r.summaryId] = mkEmpty(); });
    return s;
  });
  const [isRunning, setIsRunning] = useState(false);
  const [visible, setVisible] = useState(false);
  const lastSendEmailRef = useRef(false);

  const uploadStatesRef = useRef(uploadStates);
  useEffect(() => { uploadStatesRef.current = uploadStates; }, [uploadStates]);

  const fileInputRefs = useRef({});
  const modalRef = useRef(null);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
    setTimeout(() => modalRef.current?.focus(), 50);
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !isRunning) doClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isRunning]); // eslint-disable-line react-hooks/exhaustive-deps

  const uploadedCount  = rows.filter((r) => uploadStates[r.summaryId]?.phase === 'uploaded').length;
  const pendingCount   = rows.filter((r) => uploadStates[r.summaryId]?.phase === 'selected').length;
  const uploadingCount = rows.filter((r) => uploadStates[r.summaryId]?.phase === 'uploading').length;
  const hasUploadable  = rows.some((r) => ['selected', 'upload_failed'].includes(uploadStates[r.summaryId]?.phase) && uploadStates[r.summaryId]?.file);

  // ── Upload helpers ──────────────────────────────────────────────────────────

  const setRowState = (summaryId, patch) =>
    setUploadStates((prev) => ({ ...prev, [summaryId]: { ...prev[summaryId], ...patch } }));

  const doUploadFile = async (summaryId, customerAccount, file, sendEmail) => {
    const prevState = uploadStatesRef.current[summaryId];
    if (prevState?.objectUrl) URL.revokeObjectURL(prevState.objectUrl);
    const objectUrl = URL.createObjectURL(file);
    setUploadStates((prev) => ({
      ...prev,
      [summaryId]: { phase: 'uploading', file, objectUrl, attachmentId: null, attachmentUrl: null, errorMsg: null },
    }));
    try {
      const res = await uploadAttachment(summaryId, customerAccount, file, sendEmail);

      const httpStatus = res?.response?.status ?? res?.status;
      const isHttpError = httpStatus != null && httpStatus >= 400;
      const isApiError = res?.data?.status && res.data.status !== 'SUCCESS';
      if (res instanceof Error || isHttpError || isApiError) {
        throw new Error(res?.response?.data?.msg ?? res?.data?.msg ?? 'Upload failed');
      }

      const data = res?.data?.data ?? res?.data ?? {};
      setRowState(summaryId, { phase: 'uploaded', attachmentId: data?.attachmentId ?? null, attachmentUrl: data?.url ?? null });
    } catch {
      setRowState(summaryId, { phase: 'upload_failed', errorMsg: 'Upload failed. Try again.' });
      showErrorToast(`Upload failed for ${customerAccount}`);
    }
  };

  const handleFileSelect = (summaryId, e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    const err = validateFile(file);
    if (err) { showErrorToast(err); return; }
    const prev = uploadStatesRef.current[summaryId];
    if (prev?.objectUrl) URL.revokeObjectURL(prev.objectUrl);
    const objectUrl = URL.createObjectURL(file);
    setUploadStates((prev2) => ({
      ...prev2,
      [summaryId]: { phase: 'selected', file, objectUrl, attachmentId: null, attachmentUrl: null, errorMsg: null },
    }));
  };

  const handleRetryUpload = async (row) => {
    const s = uploadStatesRef.current[row.summaryId];
    if (!s?.file) return;
    await doUploadFile(row.summaryId, row.customerAccount, s.file, lastSendEmailRef.current);
  };

  const handleRemove = (row) => {
    const s = uploadStatesRef.current[row.summaryId];
    if (s?.objectUrl) URL.revokeObjectURL(s.objectUrl);
    setUploadStates((prev) => ({ ...prev, [row.summaryId]: mkEmpty() }));
  };

  // ── Bulk upload ─────────────────────────────────────────────────────────────

  const handleUploadAll = async (sendEmail) => {
    if (isRunning) return;
    lastSendEmailRef.current = sendEmail;
    setIsRunning(true);

    const rowsToUpload = rows.filter((r) => {
      const s = uploadStatesRef.current[r.summaryId];
      return ['selected', 'upload_failed'].includes(s?.phase) && s?.file;
    });

    await Promise.all(
      rowsToUpload.map((row) => {
        const s = uploadStatesRef.current[row.summaryId];
        return doUploadFile(row.summaryId, row.customerAccount, s.file, sendEmail);
      })
    );

    setIsRunning(false);

    const failCount = rowsToUpload.filter((r) => uploadStatesRef.current[r.summaryId]?.phase === 'upload_failed').length;
    const successCount = rows.filter((r) => uploadStatesRef.current[r.summaryId]?.phase === 'uploaded').length;
    if (successCount > 0) {
      showSuccessToast(`${rowsToUpload.length} receipt${rowsToUpload.length !== 1 ? 's' : ''} uploaded successfully`);
    }

    if (failCount === 0 && rowsToUpload.length > 0) {
      const current = uploadStatesRef.current;
      rows.forEach((r) => { if (current[r.summaryId]?.objectUrl) URL.revokeObjectURL(current[r.summaryId].objectUrl); });
      onClose();
    }
  };

  // ── Close ────────────────────────────────────────────────────────────────────

  const doClose = () => {
    if (isRunning) return;
    const current = uploadStatesRef.current;
    rows.forEach((r) => { if (current[r.summaryId]?.objectUrl) URL.revokeObjectURL(current[r.summaryId].objectUrl); });
    onClose();
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget && !isRunning) doClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1060 }}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-label="Bulk Email — COD Statements"
        tabIndex={-1}
        style={{
          maxWidth: 900, width: 'calc(100% - 32px)',
          background: '#fff', borderRadius: 16,
          boxShadow: '0 24px 60px -12px rgba(0,0,0,0.35)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          fontFamily: 'Poppins, sans-serif',
          opacity: visible ? 1 : 0,
          transform: visible ? 'scale(1)' : 'scale(0.96)',
          transition: 'opacity 0.22s ease, transform 0.22s ease',
          maxHeight: '90vh', outline: 'none',
        }}
      >
        {/* Header */}
        <div style={{ padding: '22px 24px 18px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div style={{ width: 42, height: 42, borderRadius: 11, background: '#fde7e7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Mail size={20} color={BRAND_RED} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 18, fontWeight: 600, color: '#111827', marginBottom: 4 }}>Bulk Email — COD Statements</div>
            <div style={{ fontSize: 13, color: '#6b7280' }}>Upload a statement for each customer, then click Submit.</div>
          </div>
          <button
            type="button"
            onClick={doClose}
            disabled={isRunning}
            aria-label="Close"
            style={{ border: 'none', background: 'transparent', cursor: isRunning ? 'not-allowed' : 'pointer', color: '#9ca3af', padding: 4, borderRadius: 6, display: 'flex', opacity: isRunning ? 0.4 : 1 }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Toolbar */}
        <div style={{ padding: '12px 24px', background: '#fafafa', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <span style={{ fontSize: 13, color: '#374151' }}>
            <strong style={{ color: BRAND_RED }}>{rows.length}</strong>
            {' customers selected · '}
            <strong style={{ color: BRAND_RED }}>{uploadedCount}</strong>
            {' uploaded'}
            {pendingCount > 0 && (
              <span style={{ color: '#3b82f6' }}> · {pendingCount} pending</span>
            )}
            {uploadingCount > 0 && (
              <span style={{ color: '#92400e' }}> · {uploadingCount} uploading…</span>
            )}
          </span>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button
              type="button"
              onClick={() => handleUploadAll(true)}
              disabled={!hasUploadable || isRunning}
              title={!hasUploadable ? 'Select a statement for at least one customer first.' : undefined}
              style={{
                padding: '8px 16px', borderRadius: 8, border: 'none',
                background: !hasUploadable || isRunning ? '#f3f4f6' : BRAND_RED,
                color: !hasUploadable || isRunning ? '#9ca3af' : '#fff',
                fontSize: 13, fontWeight: 600,
                cursor: !hasUploadable || isRunning ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', gap: 6,
                fontFamily: 'inherit', transition: 'all 0.15s',
              }}
            >
              {isRunning ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />}
              Submit
            </button>
          </div>
        </div>

        {/* Column headers */}
        <div style={{ display: 'grid', gridTemplateColumns: GRID_COLS, gap: GRID_GAP, padding: '10px 24px', borderBottom: '1px solid #f0f0f0', overflowY: 'scroll', scrollbarColor: 'transparent transparent' }}>
          {['#', 'Customer', 'Customer Name', 'Payable', 'Attachment / Status'].map((h) => (
            <div key={h} style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h}</div>
          ))}
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {rows.map((row, i) => {
            const us = uploadStates[row.summaryId];
            return (
              <div
                key={row.summaryId}
                style={{
                  display: 'grid', gridTemplateColumns: GRID_COLS, gap: GRID_GAP,
                  padding: '16px 24px',
                  borderBottom: i < rows.length - 1 ? '1px solid #f4f4f4' : 'none',
                  alignItems: 'center',
                }}
              >
                <div style={{ fontSize: 13, color: '#9ca3af', minWidth: 0 }}>{i + 1}</div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.customerAccount}</div>
                  <div style={{ fontSize: 12, marginTop: 2, color: row.emailSent ? '#16a34a' : '#9ca3af' }}>
                    {row.emailSent ? '✓ Emailed before' : 'Not emailed yet'}
                  </div>
                </div>
                <div style={{ fontSize: 13.5, color: '#374151', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.customerName}</div>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: Number(row.payable) < 0 ? BRAND_RED : '#111827', minWidth: 0, whiteSpace: 'nowrap' }}>
                  {Number(row.payable).toFixed(2)}
                </div>
                <div style={{ minWidth: 0 }}>
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    style={{ display: 'none' }}
                    ref={(el) => { fileInputRefs.current[row.summaryId] = el; }}
                    onChange={(e) => handleFileSelect(row.summaryId, e)}
                  />
                  <AttachmentCell
                    uploadState={us}
                    onPickFile={() => fileInputRefs.current[row.summaryId]?.click()}
                    onRetryUpload={() => handleRetryUpload(row)}
                    onView={() => {
                      const url = us?.attachmentUrl || us?.objectUrl;
                      if (url) window.open(url, '_blank');
                    }}
                    onDownload={() => {
                      const url = us?.objectUrl || us?.attachmentUrl;
                      if (!url) return;
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = us?.file?.name || 'attachment';
                      a.click();
                    }}
                    onRemove={() => handleRemove(row)}
                    locked={isRunning || row.emailSent}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default BulkEmailModal;
